import { Text, Flexbox } from '@morressier/ts';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  faShieldSlash,
  faShieldExclamation
} from '@fortawesome/pro-regular-svg-icons';

const statuses = {
  verified: {
    color: 'var(--color-mono-0)',
    bgColor: 'var(--color-teal-80)',
    tooltipColor: 'var(--color-teal-40)',
    icon: faShieldCheck,
    text: 'Verified',
    tooltipText:
      "This affiliation with a recognized organization was confirmed using Morressier's verification service."
  },
  unVerified: {
    color: 'var(--color-mono-0)',
    bgColor: 'var(--color-red-90)',
    tooltipColor: 'var(--color-red-50)',
    icon: faShieldSlash,
    text: 'Unverified',
    tooltipText: 'Affiliation verification has not been completed.'
  },
  unVerifiable: {
    color: 'var(--color-mono-40)',
    bgColor: 'var(--color-mono-95)',
    tooltipColor: 'var(--color-mono-60)',
    icon: faShieldExclamation,
    text: 'Unverifiable',
    tooltipText:
      'This affiliation was either not confirmed using the Morressier’s secure email verification service, or we cannot confirm the authenticity of this organization.'
  }
};

const Tooltip = styled.div`
  visibility: hidden;
  transition: opacity ease-out 150ms, bottom ease-out 150ms;
  position: absolute;

  padding: 8px;
  background-color: var(--color-mono-100);
  box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.16);
  border-radius: 4px;
  width: 278px;
  z-index: 1;

  top: 40px;
  left: -110px;
  &::after {
    content: ' ';
    position: absolute;
    bottom: 100%; /* At the top of the tooltip */
    left: 50%;
    margin-left: 0px;
    border-width: 12px;
    border-style: solid;
    border-color: transparent transparent var(--color-mono-100) transparent;
  }
`;

const Status = styled(Flexbox)<{
  status: 'verified' | 'unVerified' | 'unVerifiable';
}>`
  position: relative;
  padding: 2px 4px;
  gap: 4px;
  border-radius: 4px;
  align-items: center;
  ${({ status }) =>
    css`
      background-color: ${statuses[status].bgColor};
      color: ${statuses[status].color};
    `}
  &:hover {
    ${Tooltip} {
      visibility: visible;
    }
  }
`;

const TooltipStatus = styled(Flexbox)<{
  status: 'verified' | 'unVerified' | 'unVerifiable';
}>`
  padding: 2px 4px;
  gap: 4px;
  border-radius: 4px;
  align-items: center;
  margin-bottom: 4px;
  ${({ status }) =>
    css`
      color: ${statuses[status].tooltipColor};
    `}
`;

const StatusText = styled(Text)`
  color: inherit;
`;

const RinggoldText = styled(Text)`
  font-weight: 500;
  margin-top: 1rem;
  text-align: right;
  color: var(--color-mono-40);
  a {
    text-decoration: none;
    color: #1e4b9f;
    border-radius: 0.25rem;
  }
`;

interface IAffiliationItemStatusProps {
  type: 'RINGGOLD' | 'USER_PROVIDED';
  ringgold?: string;
  validatedAt: string | null;
  isSameUser: boolean;
  email: string;
}

const AffiliationItemStatus = (props: IAffiliationItemStatusProps) => {
  const { type, ringgold, validatedAt, isSameUser, email } = props;

  const getValidationStatus = () => {
    if (!validatedAt && email && type == 'RINGGOLD') {
      return 'unVerified';
    } else if ((type == 'RINGGOLD' || ringgold) && validatedAt) {
      return 'verified';
    } else {
      return 'unVerifiable';
    }
  };

  const status = getValidationStatus();

  if (!isSameUser && status !== 'verified') return null;

  return (
    <Status status={status}>
      <StatusText size="body2_new">{statuses[status].text}</StatusText>
      <FontAwesomeIcon icon={statuses[status].icon} />
      <Tooltip>
        <TooltipStatus status={status}>
          <StatusText size="body2_new">{statuses[status].text}</StatusText>
          <FontAwesomeIcon icon={statuses[status].icon} />
        </TooltipStatus>
        <Text size="body2_new">{statuses[status].tooltipText}</Text>
        <RinggoldText size="body2_new">
          Powered by{' '}
          <a
            href="https://www.ringgold.com/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Ringgold&trade;
          </a>
        </RinggoldText>
      </Tooltip>
    </Status>
  );
};

export default AffiliationItemStatus;
